import { useEffect, useState, useMemo } from 'react'
import { addressService, Address } from '@/services/addressService'
import { DetailHeader } from '@/components/ui/DetailHeader'
import DataTable from '@/components/ui/DataTable'
import FilterBar from '@/components/ui/FilterBar'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { RefreshCw, Home, Briefcase, MapPin, Plus } from 'lucide-react'
import { GoogleMapsConfigProvider } from '@/components/maps/GoogleMapsConfigProvider'
import { GoogleMapsStaticMap } from '@/components/maps/GoogleMapsStaticMap'
import { GoogleMapsAddressPicker } from '@/components/maps/GoogleMapsAddressPicker'
import { ParsedAddress } from '@/components/maps/types'

const AddressesPage = () => {
  const [addresses, setAddresses] = useState<Address[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeFilters, setActiveFilters] = useState<any[]>([])
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0
  })
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [editingAddress, setEditingAddress] = useState<Address | null>(null)
  const [newAddress, setNewAddress] = useState({
    user_id: '',
    type: 'home' as 'home' | 'work' | 'other',
    street: '',
    city: '',
    postal_code: '',
    country: '',
    latitude: undefined as number | undefined,
    longitude: undefined as number | undefined,
    is_default: false
  })
  const [selectedAddressForMap, setSelectedAddressForMap] = useState<Address | null>(null)

  const loadAddresses = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await addressService.getAll()
      if (response.success) {
        setAddresses(response.data)
        setPagination(prev => ({
          ...prev,
          total: response.data.length
        }))
      } else {
        setError('Échec du chargement des adresses')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAddresses()
  }, [])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'home': return <Home className="h-3 w-3 mr-1" />
      case 'work': return <Briefcase className="h-3 w-3 mr-1" />
      default: return <MapPin className="h-3 w-3 mr-1" />
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'home': return 'Domicile'
      case 'work': return 'Travail'
      default: return 'Autre'
    }
  }

  const columns = useMemo(() => [
    {
      key: 'user',
      label: 'Utilisateur',
      sortable: true,
      render: (row: Address) => (
        <div className="font-medium">
          {row?.user_first_name && row?.user_last_name
            ? `${row.user_first_name} ${row.user_last_name}`
            : `Utilisateur ${row?.user_id?.substring(0, 8) || 'ID inconnu'}...`}
        </div>
      )
    },
    {
      key: 'street',
      label: 'Adresse',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm">{value}</div>
      )
    },
    {
      key: 'city',
      label: 'Ville',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm">{value}</div>
      )
    },
    {
      key: 'postal_code',
      label: 'Code postal',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm">{value}</div>
      )
    },
    {
      key: 'country',
      label: 'Pays',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm">{value}</div>
      )
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      render: (value: string) => (
        <Badge variant="outline" className="inline-flex items-center">
          {getTypeIcon(value)}
          {getTypeLabel(value)}
        </Badge>
      )
    },
    {
      key: 'created_at',
      label: 'Créé le',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm">{formatDate(value)}</div>
      )
    },
    {
      key: 'is_default',
      label: 'Statut',
      sortable: true,
      render: (value: boolean) => (
        <Badge variant={value ? 'default' : 'secondary'}>
          {value ? 'Par défaut' : 'Secondaire'}
        </Badge>
      )
    }
  ], [])

  const actions = useMemo(() => [
    {
      label: 'Voir sur la carte',
      icon: <MapPin className="h-4 w-4" />,
      onClick: (row: Address) => {
        if (row.latitude && row.longitude) {
          setSelectedAddressForMap(row)
        } else {
          alert('Cette adresse n\'a pas de coordonnées géographiques')
        }
      }
    },
    {
      label: 'Modifier',
      icon: <Briefcase className="h-4 w-4" />,
      onClick: (row: Address) => {
        setEditingAddress(row)
        setShowEditForm(true)
        // Pre-fill the form with the address data
        setNewAddress({
          user_id: row.user_id || '',
          type: row.type as 'home' | 'work' | 'other',
          street: row.street || '',
          city: row.city || '',
          postal_code: row.postal_code || '',
          country: row.country || '',
          latitude: row.latitude ?? undefined,
          longitude: row.longitude ?? undefined,
          is_default: row.is_default || false
        })
      }
    }
  ], [])

  const filterOptions = useMemo(() => [
    {
      key: 'type',
      label: 'Type',
      type: 'select' as const,
      options: [
        { value: 'all', label: 'Tous les types' },
        { value: 'home', label: 'Domicile' },
        { value: 'work', label: 'Travail' },
        { value: 'other', label: 'Autre' }
      ]
    },
    {
      key: 'is_default',
      label: 'Statut',
      type: 'select' as const,
      options: [
        { value: 'all', label: 'Tous les statuts' },
        { value: 'true', label: 'Par défaut' },
        { value: 'false', label: 'Secondaire' }
      ]
    }
  ], [])

  const handleFilterChange = (newFilters: any[]) => {
    setActiveFilters(newFilters)
  }

  const handleSearch = (value: string) => {
    setSearchQuery(value)
  }

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }))
  }

  const handlePageSizeChange = (newSize: number) => {
    setPagination(prev => ({ ...prev, pageSize: newSize, page: 1 }))
  }

  const filteredData = useMemo(() => {
    let result = addresses

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(address => 
        address.street.toLowerCase().includes(query) || 
        address.city.toLowerCase().includes(query) ||
        address.postal_code.toLowerCase().includes(query) ||
        address.country.toLowerCase().includes(query) ||
        (address.user_first_name?.toLowerCase() || '').includes(query) ||
        (address.user_last_name?.toLowerCase() || '').includes(query)
      )
    }

    // Apply other filters
    activeFilters.forEach(filter => {
      if (filter.key === 'type' && filter.value !== 'all') {
        result = result.filter(address => address.type === filter.value)
      }
      if (filter.key === 'is_default' && filter.value !== 'all') {
        const isDefault = filter.value === 'true'
        result = result.filter(address => address.is_default === isDefault)
      }
    })

    // Update pagination total
    setPagination(prev => ({ ...prev, total: result.length }))

    return result
  }, [addresses, searchQuery, activeFilters])

  const paginatedData = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.pageSize
    const endIndex = startIndex + pagination.pageSize
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, pagination.page, pagination.pageSize])

  const breadcrumbs = [
    { label: 'Tableau de bord', href: '/' },
    { label: 'Adresses', href: '/addresses' }
  ]

  const headerActions = [
    {
      label: 'Nouvelle adresse',
      icon: <Plus className="h-4 w-4 mr-2" />,
      onClick: () => setShowCreateForm(true),
      variant: 'default' as const,
      disabled: loading
    },
    {
      label: 'Actualiser',
      icon: <RefreshCw className="h-4 w-4 mr-2" />,
      onClick: loadAddresses,
      variant: 'outline' as const,
      disabled: loading
    }
  ]

  const stats = useMemo(() => ({
    total: addresses.length,
    default: addresses.filter(a => a.is_default).length,
    types: new Set(addresses.map(a => a.type)).size
  }), [addresses])

  return (
    <div className="space-y-6">
      <DetailHeader
        title="Adresses"
        subtitle="Gérez les adresses des utilisateurs et commerçants"
        breadcrumbs={breadcrumbs}
        actions={headerActions}
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
          <div className="mt-4">
            <Button variant="outline" onClick={loadAddresses}>
              Réessayer
            </Button>
          </div>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total adresses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Adresses par défaut</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.default}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Types d'adresses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.types}</div>
          </CardContent>
        </Card>
      </div>

      {/* Create Address Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">Nouvelle adresse avec Google Maps</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCreateForm(false)}
                >
                  ×
                </Button>
              </div>
              
              <GoogleMapsConfigProvider config={{ apiKey: "AIzaSyBrNFuKMuE48eOWeg8V9Y9_3wRXO0KSJKQ", libraries: ['places'], language: 'fr' }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rechercher l'adresse avec Google Maps
                    </label>
                    <GoogleMapsAddressPicker
                      onChange={(address: ParsedAddress | null) => {
                        if (address) {
                          setNewAddress(prev => ({
                            ...prev,
                            street: address.address_line1 || '',
                            city: address.city || '',
                            postal_code: address.postal_code || '',
                            country: address.country || '',
                            latitude: address.latitude,
                            longitude: address.longitude
                          }))
                        }
                      }}
                      placeholder="Commencez à taper une adresse..."
                      className="w-full"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rue
                      </label>
                      <input
                        type="text"
                        value={newAddress.street}
                        onChange={(e) => setNewAddress(prev => ({ ...prev, street: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ville
                      </label>
                      <input
                        type="text"
                        value={newAddress.city}
                        onChange={(e) => setNewAddress(prev => ({ ...prev, city: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Code postal
                      </label>
                      <input
                        type="text"
                        value={newAddress.postal_code}
                        onChange={(e) => setNewAddress(prev => ({ ...prev, postal_code: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pays
                      </label>
                      <input
                        type="text"
                        value={newAddress.country}
                        onChange={(e) => setNewAddress(prev => ({ ...prev, country: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <select
                        value={newAddress.type}
                        onChange={(e) => setNewAddress(prev => ({ ...prev, type: e.target.value as 'home' | 'work' | 'other' }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="home">Domicile</option>
                        <option value="work">Travail</option>
                        <option value="other">Autre</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ID Utilisateur
                      </label>
                      <input
                        type="text"
                        value={newAddress.user_id}
                        onChange={(e) => setNewAddress(prev => ({ ...prev, user_id: e.target.value }))}
                        placeholder="Entrez l'ID de l'utilisateur"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <input
                        type="checkbox"
                        checked={newAddress.is_default}
                        onChange={(e) => setNewAddress(prev => ({ ...prev, is_default: e.target.checked }))}
                        className="mr-2"
                      />
                      Adresse par défaut
                    </label>
                  </div>

                  {newAddress.latitude && newAddress.longitude && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Aperçu de la carte
                      </label>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <GoogleMapsStaticMap
                          latitude={newAddress.latitude}
                          longitude={newAddress.longitude}
                          zoom={15}
                          width={600}
                          height={200}
                          apiKey="AIzaSyBrNFuKMuE48eOWeg8V9Y9_3wRXO0KSJKQ"
                        />
                        <div className="mt-2 text-sm text-gray-600">
                          Coordonnées: {newAddress.latitude.toFixed(6)}, {newAddress.longitude.toFixed(6)}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 pt-6">
                    <Button
                      variant="outline"
                      onClick={() => setShowCreateForm(false)}
                    >
                      Annuler
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          // Call addressService.create with the new address data
                          const response = await addressService.create({
                            user_id: newAddress.user_id,
                            type: newAddress.type,
                            street: newAddress.street,
                            city: newAddress.city,
                            postal_code: newAddress.postal_code,
                            country: newAddress.country,
                            latitude: newAddress.latitude,
                            longitude: newAddress.longitude,
                            is_default: newAddress.is_default
                          })
                          
                          if (response.success) {
                            setShowCreateForm(false)
                            // Reset form
                            setNewAddress({
                              user_id: '',
                              type: 'home',
                              street: '',
                              city: '',
                              postal_code: '',
                              country: '',
                              latitude: undefined,
                              longitude: undefined,
                              is_default: false
                            })
                            // Refresh addresses
                            loadAddresses()
                          } else {
                            setError(`Erreur lors de la création: ${response.message}`)
                          }
                        } catch (err) {
                          console.error('Error creating address:', err)
                          setError(err instanceof Error ? err.message : 'Une erreur est survenue lors de la création')
                        }
                      }}
                    >
                      Créer l'adresse
                    </Button>
                  </div>
                </div>
              </GoogleMapsConfigProvider>
            </div>
          </div>
        </div>
      )}

      {/* Edit Address Form Modal */}
      {showEditForm && editingAddress && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">Modifier l'adresse</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowEditForm(false)
                    setEditingAddress(null)
                    // Reset form
                    setNewAddress({
                      user_id: '',
                      type: 'home',
                      street: '',
                      city: '',
                      postal_code: '',
                      country: '',
                      latitude: undefined,
                      longitude: undefined,
                      is_default: false
                    })
                  }}
                >
                  ×
                </Button>
              </div>
              
              <GoogleMapsConfigProvider config={{ apiKey: "AIzaSyBrNFuKMuE48eOWeg8V9Y9_3wRXO0KSJKQ", libraries: ['places'], language: 'fr' }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rechercher l'adresse avec Google Maps
                    </label>
                    <GoogleMapsAddressPicker
                      onChange={(address: ParsedAddress | null) => {
                        if (address) {
                          setNewAddress(prev => ({
                            ...prev,
                            street: address.address_line1 || '',
                            city: address.city || '',
                            postal_code: address.postal_code || '',
                            country: address.country || '',
                            latitude: address.latitude,
                            longitude: address.longitude
                          }))
                        }
                      }}
                      placeholder="Commencez à taper une adresse..."
                      className="w-full"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rue
                      </label>
                      <input
                        type="text"
                        value={newAddress.street}
                        onChange={(e) => setNewAddress(prev => ({ ...prev, street: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ville
                      </label>
                      <input
                        type="text"
                        value={newAddress.city}
                        onChange={(e) => setNewAddress(prev => ({ ...prev, city: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Code postal
                      </label>
                      <input
                        type="text"
                        value={newAddress.postal_code}
                        onChange={(e) => setNewAddress(prev => ({ ...prev, postal_code: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pays
                      </label>
                      <input
                        type="text"
                        value={newAddress.country}
                        onChange={(e) => setNewAddress(prev => ({ ...prev, country: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <select
                        value={newAddress.type}
                        onChange={(e) => setNewAddress(prev => ({ ...prev, type: e.target.value as 'home' | 'work' | 'other' }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="home">Domicile</option>
                        <option value="work">Travail</option>
                        <option value="other">Autre</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <input
                          type="checkbox"
                          checked={newAddress.is_default}
                          onChange={(e) => setNewAddress(prev => ({ ...prev, is_default: e.target.checked }))}
                          className="mr-2"
                        />
                        Adresse par défaut
                      </label>
                    </div>
                  </div>

                  {newAddress.latitude && newAddress.longitude && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Aperçu de la carte
                      </label>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <GoogleMapsStaticMap
                          latitude={newAddress.latitude}
                          longitude={newAddress.longitude}
                          zoom={15}
                          width={600}
                          height={200}
                          apiKey="AIzaSyBrNFuKMuE48eOWeg8V9Y9_3wRXO0KSJKQ"
                        />
                        <div className="mt-2 text-sm text-gray-600">
                          Coordonnées: {newAddress.latitude?.toFixed(6)}, {newAddress.longitude?.toFixed(6)}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 pt-6">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowEditForm(false)
                        setEditingAddress(null)
                        // Reset form
                        setNewAddress({
                          user_id: '',
                          type: 'home',
                          street: '',
                          city: '',
                          postal_code: '',
                          country: '',
                          latitude: undefined,
                          longitude: undefined,
                          is_default: false
                        })
                      }}
                    >
                      Annuler
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          if (!editingAddress) return
                          
                          // Call addressService.update with the updated address data
                          const response = await addressService.update(editingAddress.address_id, {
                            type: newAddress.type,
                            street: newAddress.street,
                            city: newAddress.city,
                            postal_code: newAddress.postal_code,
                            country: newAddress.country,
                            latitude: newAddress.latitude,
                            longitude: newAddress.longitude,
                            is_default: newAddress.is_default
                          })
                          
                          if (response.success) {
                            setShowEditForm(false)
                            setEditingAddress(null)
                            // Reset form
                            setNewAddress({
                              user_id: '',
                              type: 'home',
                              street: '',
                              city: '',
                              postal_code: '',
                              country: '',
                              latitude: undefined,
                              longitude: undefined,
                              is_default: false
                            })
                            // Refresh addresses
                            loadAddresses()
                          } else {
                            setError(`Erreur lors de la modification: ${response.message}`)
                          }
                        } catch (err) {
                          console.error('Error updating address:', err)
                          setError(err instanceof Error ? err.message : 'Une erreur est survenue lors de la modification')
                        }
                      }}
                    >
                      Mettre à jour l'adresse
                    </Button>
                  </div>
                </div>
              </GoogleMapsConfigProvider>
            </div>
          </div>
        </div>
      )}

      {/* Map Viewer Modal */}
      {selectedAddressForMap && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">Adresse sur la carte</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedAddressForMap(null)}
                >
                  ×
                </Button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium mb-2">Informations de l'adresse</h4>
                  <p className="text-sm">
                    {selectedAddressForMap.street}, {selectedAddressForMap.postal_code} {selectedAddressForMap.city}, {selectedAddressForMap.country}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Coordonnées: {Number(selectedAddressForMap.latitude)?.toFixed(6)}, {Number(selectedAddressForMap.longitude)?.toFixed(6)}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <GoogleMapsConfigProvider config={{ apiKey: "AIzaSyBrNFuKMuE48eOWeg8V9Y9_3wRXO0KSJKQ", libraries: ['places'], language: 'fr' }}>
                    <GoogleMapsStaticMap
                      latitude={selectedAddressForMap.latitude!}
                      longitude={selectedAddressForMap.longitude!}
                      zoom={15}
                      width={800}
                      height={400}
                      apiKey="AIzaSyBrNFuKMuE48eOWeg8V9Y9_3wRXO0KSJKQ"
                    />
                  </GoogleMapsConfigProvider>
                </div>

                <div className="flex justify-end pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedAddressForMap(null)}
                  >
                    Fermer
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <FilterBar
        searchPlaceholder="Rechercher par adresse, ville, code postal..."
        filters={filterOptions}
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
      />

      <DataTable
        columns={columns}
        data={paginatedData}
        actions={actions}
        loading={loading}
        emptyMessage="Aucune adresse trouvée"
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange
        }}
      />
    </div>
  )
}

export default AddressesPage
